import React, { useCallback, useEffect, useState, useRef } from 'react';
import '../../assets/scss/common.scss'; // variables, mixins, etc. are imported here
import '../../assets/reset.css';
import RotateSwiperSection from './components/RotateSwiperSection.tsx';
import AnalyzeSwiperSection from './components/AnalyzeSwiperSection.tsx';
import BottomSheet from './components/BottomSheet.tsx';
import { sendEvent } from '@/api/nativeBridge.ts';
import { useSearchQuery } from '@/queries/hooks/useApiQuery.ts';
import { logger } from '@/utils/logger.ts';
import { CallList } from './types.ts';
import SearchNodata from './components/SearchNodata.tsx';
import { useNavigate } from 'react-router-dom';
import { safeFormatDateTime } from '@/utils/dateUtils.ts';
import { useTokenStore, useTheme } from '@/hooks/useStore.ts';
import SearchLoading from './components/SearchLoading.tsx';
import useBridgeEventEffect from '@/hooks/bridge/useBridgeEventEffect.ts';
import useElementRectOnMutation from '@/hooks/useElementRectOnMutation.ts';

function Search() {
  const theme = useTheme();
  const [searchList, setSearchList] = useState<CallList>({ callList: [] });
  const [topOffset, setTopOffset] = useState<number>(0); // 초기값 없음
  const token = useTokenStore.getState().token;
  const navigate = useNavigate();
  const { data, isLoading, isSuccess, isError, error, refetch } =
    useSearchQuery();

  const rotateSwiperRef = useRef<HTMLDivElement>(null);

  // -----------------------------
  //  콜백 정의
  // -----------------------------
  const handleGetNameCallback = useCallback((event: CustomEvent) => {
    const names = event.detail.names;

    logger.debug('names : ', names);
    setSearchList((prevList) => ({
      ...prevList,
      callList: prevList.callList.map((call) => ({
        ...call,
        name:
          names[call.callId]?.toLowerCase() === 'unknown'
            ? '알 수 없음'
            : names[call.callId] || '알 수 없음',
        isValidCall:
          names[call.callId]?.toLowerCase() !== 'unknown' &&
          names[call.callId]?.length > 0
            ? true
            : false,
      })),
    }));
  }, []);

  const handleRefreshViewCallback = useCallback(
    (event: CustomEvent) => {
      const isAgreed = event.detail.isAgreed;
      if (isAgreed === false) {
        navigate('/');
      } else {
        refetch().then(() => {
          if (rotateSwiperRef.current) {
            // RotateSwiperSection의 위치를 계산
            setBottomSheetLocation();
          }
        });
      }
    },
    [navigate, refetch],
  );

  useBridgeEventEffect('GET_NAME_CALLBACK', handleGetNameCallback);
  useBridgeEventEffect('REFRESH_VIEW_CALLBACK', handleRefreshViewCallback);

  const setBottomSheetLocation = () => {
    let retryCount = 0;
    const intervalTimer = setInterval(function () {
      logger.debug(retryCount++);
      if (retryCount > 20) {
        clearInterval(intervalTimer);
      }
      if (document.querySelector('.card-item')) {
        const swiperContainerEl = document.querySelector(
          '.ixi-O-container .swiper.rotateSwiper .swiper-initialized',
        );
        const targetEl = swiperContainerEl;
        if (targetEl) {
          const paginationBottom = targetEl.getBoundingClientRect().bottom;
          setTopOffset(paginationBottom); // BottomSheet의 위치를 초기화
          clearInterval(intervalTimer);
        }
      }
    }, 100);
  };

  // ============ api 데이터 처리 ================
  // 최초, data 들어 왔을때
  useEffect(() => {
    logger.debug('data :: ', data);
    //undefined 였다가 값이 없으면 null 이 됩니다.
    if (data === undefined) return;

    if (data?.callList?.length > 0) {
      // TODO: callId 배열로 GET_NAME 하고 데이터 받음
      sendEvent('GET_NAME', {
        callIds: data.callList.map((call: any) => call.callId),
      });
      setSearchList(data);
    }
  }, [data]);

  useElementRectOnMutation(rotateSwiperRef, (rect) => {
    setTopOffset(rect.bottom);
  });

  if (isLoading || data === undefined || token === undefined) {
    return <SearchLoading />;
  }

  if (isSuccess && data?.callList?.length == 0)
    return <SearchNodata></SearchNodata>;

  if (isError) {
    logger.error(error);
    return <SearchNodata></SearchNodata>;
  }

  return (
    <div className={`ixi-O-container ${theme}`}>
      {isSuccess && data?.callList?.length > 0 && (
        <div className="title-wrap">
          <picture>
            <img src="/res/img/ai-tips.png" alt="" />
          </picture>
          <h2 className="title-wrap__title">
            {searchList.callList?.[0]?.pushBody}
          </h2>
          <p className="title-wrap__use-info">
            <span
              className="user"
              onClick={() => {
                if (searchList.callList?.[0]?.isValidCall) {
                  sendEvent('SEND_LOG', {
                    type: 'GA4',
                    key: 'AI검색_최근대화',
                    message: '통화이력 더보기',
                  });
                  sendEvent('GO_DISCOVER_SUMMARY', {
                    callId: searchList.callList?.[0]?.callId,
                  });
                } else {
                  alert('삭제된 통화이력 입니다.');
                }
              }}
            >
              <em>{searchList.callList?.[0]?.name}</em>
              <span>&nbsp;님과 통화</span>
            </span>
            <span className="time">
              {safeFormatDateTime(searchList.callList?.[0]?.callStartTime)}
            </span>
          </p>
        </div>
      )}
      <RotateSwiperSection
        ref={rotateSwiperRef}
        callList={[searchList.callList?.[0]]}
      />
      {topOffset > 0 && searchList?.callList?.length > 1 && (
        <BottomSheet initialOffsetY={topOffset}>
          <AnalyzeSwiperSection callList={searchList.callList.slice(1)} />
        </BottomSheet>
      )}
    </div>
  );
}

export default Search;
